/*
 *
 *  * Copyright(c) 2021 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import * as _ from 'underscore';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { SortPipe } from '../../pipes/sort.pipe';
import { SearchPipe } from '../../pipes/search.pipe';
import { SortOrder } from '../../types/sort-order';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DOCUMENT } from '@angular/common';
import { ChangeDetector } from '../../change-detector';
import { TranslateService } from '@ngx-translate/core';
import { isContainsAny, isEmpty, isSubsetOf } from '@core/lib/helpers/helpers';

@Component({
  selector: 'wfm-swap-list',
  templateUrl: './swap-list.component.html',
  styleUrls: ['./swap-list.component.scss'],
  providers: [SearchPipe, SortPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SwapListComponent extends ChangeDetector implements OnInit, AfterViewInit, OnDestroy {
  @Input() displayProperty: string = undefined;
  @Input() sortOrder = SortOrder.ASC;
  @Input() rowHeight = 40;
  @Input() locale: string;
  @Input() source: any[];
  @Input() destination: any[];
  @Input() groupByItems: Array<string | number>;
  @Input() groupByValue: any;
  @Input() groupByPropertyName: string;
  @Input() enableGroupByFilter = false;
  @Input() height = 350;
  @Input() fromHeaderLabel = '';
  @Input() toHeaderLabel = '';
  @Input() filterTooltip;
  @Input() groupByFilterPlaceholder = 'core.label.filterBy';
  @Input() textFilterPlaceholder = 'core.label.search';
  @Input() matchAllGroupItems = true;
  @Output() destinationChange = new EventEmitter<any[]>();
  @Output() fromErrorNotify = new EventEmitter<{ message: any; error: any }>();
  @Output() toErrorNotify = new EventEmitter<{ message: any; error: any }>();

  @ViewChildren('fromSelectedElement') fromSelectedElements: QueryList<ElementRef>;
  @ViewChildren('toSelectedElement') toSelectedElements: QueryList<ElementRef>;
  @ViewChild('swapListDivider') swapListDivider: ElementRef<HTMLElement>;
  @ViewChild('fromHeader') fromHeader: ElementRef<HTMLElement>;
  @ViewChild('fromItemsViewPort') fromItemsViewPort: CdkVirtualScrollViewport;
  @ViewChild('toItemsViewPort') toItemsViewPort: CdkVirtualScrollViewport;

  groupByFilterValue: any[];
  fromFilteredItems: any[] = [];
  toFilteredItems: any[] = [];
  filterText: string;
  scroll = {
    from: {
      verticalOffsetSize: 0,
      verticalScrollSize: 0,
      verticalScrollPosition: 0,
      verticalScrollHandlerTimer: 0,
      verticalScrollCalculation: undefined,
      scrolledByApplication: 0,
      wheelTimer: 0,
      wheelDelta: 0
    },
    to: {
      verticalOffsetSize: 0,
      verticalScrollSize: 0,
      verticalScrollPosition: 0,
      verticalScrollHandlerTimer: 0,
      verticalScrollCalculation: undefined,
      scrolledByApplication: 0,
      wheelTimer: 0,
      wheelDelta: 0
    }
  };

  fromItemsIndexMap = new Map<any, number>();
  toItemsIndexMap = new Map<any, number>();
  fromSelectedItemsMap = new Map<any, number>();
  toSelectedItemsMap = new Map<any, number>();

  private loadingTimeout;

  constructor(
    @Inject(DOCUMENT) private document: any,
    cd: ChangeDetectorRef,
    private translate: TranslateService,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private searchPipe: SearchPipe,
    private sortPipe: SortPipe
  ) {
    super(document.defaultView, cd);
  }

  ngOnInit(): void {
    if (this.height < 200) {
      this.height = 200;
    }

    this.source = this.source || [];
    this.destination = this.destination || [];
    this.createItemsIndexMap();
    this.setDisplayItems(this.source, this.destination);
    this.$detectChanges();
  }

  ngAfterViewInit(): void {
    this.renderer.setStyle(
      this.swapListDivider?.nativeElement,
      'margin-top',
      `${this.fromHeader?.nativeElement?.offsetHeight ? this.fromHeader.nativeElement.offsetHeight + 16 : 38}px`
    );
    this.runScrollHandler();
    this.calculateVerticalScrolls();
    this.$detectChanges();
  }

  ngOnDestroy(): void {
    this.toFilteredItems.length = 0;
    this.fromFilteredItems.length = 0;
    this.toSelectedItemsMap.clear();
    this.fromSelectedItemsMap.clear();
    this.toItemsIndexMap.clear();
    this.fromItemsIndexMap.clear();
    this.window.clearTimeout(this.scroll.from.wheelTimer);
    this.window.clearTimeout(this.scroll.to.wheelTimer);
  }

  toggleSelect = (item: any, rtl = false) => {
    if (rtl) {
      if (this.toSelectedItemsMap.get(item) === undefined) {
        this.toSelectedItemsMap.set(item, this.toItemsIndexMap.get(item));
      } else {
        this.toSelectedItemsMap.delete(item);
      }
    } else {
      if (this.fromSelectedItemsMap.get(item) === undefined) {
        this.fromSelectedItemsMap.set(item, this.fromItemsIndexMap.get(item));
      } else {
        this.fromSelectedItemsMap.delete(item);
      }
    }
  };

  filterItems() {
    if (isEmpty(this.filterText?.trim())) {
      this.filterText = undefined;
    }

    this.setDisplayedItems();
    this.runScrollHandler();
  }

  private setDisplayedItems() {
    this.filterItemsThrottle(this.source, this.destination);
  }

  private groupByFilter(input: any[]): any[] {
    if (!this.groupByFilterValue) return input;
    if (this.groupByFilterValue?.length > 0) {
      const value = [];
      if (this.matchAllGroupItems) {
        for (let i = 0, len = input.length; i < len; i++) {
          const groups: any[] = input[i][this.groupByPropertyName];
          if (isSubsetOf(this.groupByFilterValue, groups)) {
            value.push(input[i]);
          }
        }
      } else {
        for (let i = 0, len = input.length; i < len; i++) {
          const groups: any[] = input[i][this.groupByPropertyName];
          if (isContainsAny(this.groupByFilterValue, groups)) {
            value.push(input[i]);
          }
        }
      }
      return value;
    }
    return input;
  }

  shuffleItems(rtl = false) {
    const selectedItems: any[] = rtl
      ? Array.from(this.toSelectedItemsMap.keys())
      : Array.from(this.fromSelectedItemsMap.keys());

    if (selectedItems && selectedItems.length) {
      selectedItems.forEach(item => {
        if (rtl) {
          this.source.push(item);
          this.destination.splice(
            this.destination.findIndex(itm => itm === item),
            1
          );
        } else {
          this.destination.push(item);
          this.source.splice(
            this.source.findIndex(itm => itm === item),
            1
          );
        }
      });

      this.createItemsIndexMap();

      this.filterItems();
      this.destinationChange.emit(this.destination);
      this.toSelectedItemsMap.clear();
      this.fromSelectedItemsMap.clear();
    }
  }

  selectAll(isFromItems = true) {
    const items = isFromItems ? this.fromFilteredItems : this.toFilteredItems;
    const selectedItemsMap = isFromItems ? this.fromSelectedItemsMap : this.toSelectedItemsMap;
    const indexMap = isFromItems ? this.fromItemsIndexMap : this.toItemsIndexMap;
    if (items?.length) {
      for (const item of items) {
        selectedItemsMap.set(item, indexMap.get(item));
      }
    }
  }

  deselectAll(isFromItems = true) {
    const items = isFromItems ? this.fromFilteredItems : this.toFilteredItems;
    const selectedItemsMap = isFromItems ? this.fromSelectedItemsMap : this.toSelectedItemsMap;
    for (const item of items) {
      selectedItemsMap.delete(item);
    }
  }

  removeFilter(): void {
    this.filterText = '';
    this.filterItems();
  }

  verticalScrollPositionChange(position: number, isFromScroll = true) {
    const scroll = isFromScroll ? this.scroll.from : this.scroll.to;
    scroll.verticalScrollPosition = position;
    this.scrollToOffset(position, isFromScroll);
  }

  scrollToOffset(offset: number, isFromScroll: boolean) {
    if (isFromScroll) {
      this.fromItemsViewPort.scrollToOffset(offset, 'auto');
      this.window.clearTimeout(this.scroll.from.scrolledByApplication);
      this.scroll.from.scrolledByApplication = this.window.setTimeout(() => {
        this.scroll.from.scrolledByApplication = 0;
      }, 500);
    } else {
      this.toItemsViewPort.scrollToOffset(offset, 'auto');
      this.window.clearTimeout(this.scroll.to.scrolledByApplication);
      this.scroll.to.scrolledByApplication = this.window.setTimeout(() => {
        this.scroll.to.scrolledByApplication = 0;
      }, 500);
    }
  }

  scrollHandler = ($event: any, isFromScroll = true) => {
    const scroll = isFromScroll ? this.scroll.from : this.scroll.to;
    if (scroll.scrolledByApplication && $event.target.scrollTop !== scroll.verticalScrollPosition) {
      $event.target.scrollTop = scroll.verticalScrollPosition;
      $event.stopPropagation();
      $event.preventDefault();
      return;
    }
  };

  onWheel($event: WheelEvent, isFromScroll = true) {
    if (!$event.deltaY) {
      return;
    }

    $event.preventDefault();
    $event.stopPropagation();
    if (isFromScroll) {
      this.scroll.from.verticalScrollPosition += $event.deltaY;
      this.scroll.from.wheelDelta += $event.deltaY;
      this.scroll.from.verticalScrollPosition = Math.min(
        this.scroll.from.verticalScrollSize - this.scroll.from.verticalOffsetSize,
        Math.max(0, this.scroll.from.verticalScrollPosition)
      );
      if (!this.scroll.from.wheelTimer) {
        this.scroll.from.wheelTimer = this.window.setTimeout(() => {
          this.scrollToOffset(
            this.fromItemsViewPort.elementRef.nativeElement.scrollTop + this.scroll.from.wheelDelta,
            true
          );
          this.scroll.from.wheelTimer = 0;
          this.scroll.from.wheelDelta = 0;
        }, 100);
      }
    } else {
      if (!this.scroll.to.wheelTimer) {
        this.scroll.to.verticalScrollPosition += $event.deltaY;
        this.scroll.to.wheelDelta += $event.deltaY;
        this.scroll.to.verticalScrollPosition = Math.min(
          this.scroll.to.verticalScrollSize - this.scroll.to.verticalOffsetSize,
          Math.max(0, this.scroll.to.verticalScrollPosition)
        );
        this.scroll.to.wheelTimer = this.window.setTimeout(() => {
          this.scrollToOffset(
            this.toItemsViewPort.elementRef.nativeElement.scrollTop + this.scroll.to.wheelDelta,
            false
          );
          this.scroll.to.wheelTimer = 0;
          this.scroll.to.wheelDelta = 0;
        }, 100);
      }
    }
  }

  private filterItemsThrottle = _.throttle((source: any[], destination: any[]) => {
    this.clearLoadingTimout();

    this.toFilteredItems = this.sortPipe.transform(
      this.groupByFilter(
        this.searchPipe.transform(
          destination && destination.length ? [...destination] : [],
          this.filterText,
          this.displayProperty
        )
      ),
      this.sortOrder,
      this.displayProperty
    );
    this.fromFilteredItems = this.sortPipe.transform(
      this.groupByFilter(this.searchPipe.transform(source, this.filterText, this.displayProperty)),
      this.sortOrder,
      this.displayProperty
    );

    this.calculateVerticalScrolls();
    this.$detectChanges();
  }, 1000);

  private clearLoadingTimout() {
    if (this.loadingTimeout) {
      this.window.clearTimeout(this.loadingTimeout);
      this.loadingTimeout = undefined;
    }
  }

  private setDisplayItems(source: any[], destination: any[]) {
    this.filterItemsThrottle(source, destination);
  }

  private runScrollHandler(interval = 0) {
    this.scroll.from.verticalScrollHandlerTimer &&
      this.window.clearTimeout(this.scroll.from.verticalScrollHandlerTimer);
    this.scroll.from.verticalScrollHandlerTimer = this.window.setTimeout(() => {
      if (!this.scroll.from.wheelTimer) {
        this.scroll.from.verticalScrollPosition = this.fromItemsViewPort?.elementRef?.nativeElement?.scrollTop;
      }
    }, interval);

    this.scroll.to.verticalScrollHandlerTimer && this.window.clearTimeout(this.scroll.to.verticalScrollHandlerTimer);
    this.scroll.to.verticalScrollHandlerTimer = this.window.setTimeout(() => {
      if (!this.scroll.to.wheelTimer) {
        this.scroll.to.verticalScrollPosition = this.toItemsViewPort?.elementRef?.nativeElement?.scrollTop;
      }
    }, interval);
  }

  private createItemsIndexMap() {
    let index = 0;
    this.fromItemsIndexMap.clear();
    if (this.source?.length) {
      for (const item of this.source) {
        this.fromItemsIndexMap.set(item, index++);
      }
    }
    index = 0;
    this.toItemsIndexMap.clear();
    if (this.destination?.length) {
      for (const item of this.destination) {
        this.toItemsIndexMap.set(item, index++);
      }
    }
  }

  private calculateVerticalScrolls() {
    this.calculateVerticalScroll(this.scroll.from, this.fromItemsViewPort);
    this.calculateVerticalScroll(this.scroll.to, this.toItemsViewPort);
  }

  private calculateVerticalScroll(scroll: any, scrollViewPort: CdkVirtualScrollViewport) {
    scroll.verticalScrollCalculation && this.window.clearTimeout(scroll.verticalScrollCalculation);

    scroll.verticalScrollCalculation = this.window.setTimeout(() => {
      scroll.verticalOffsetSize = 0;
      scroll.verticalScrollSize = 0;
      scroll.verticalScrollPosition = 0;

      const element: HTMLElement = scrollViewPort?.elementRef?.nativeElement;
      if (element) {
        scroll.verticalOffsetSize = element.offsetHeight;
        scroll.verticalScrollSize = element.scrollHeight;
        scroll.verticalScrollPosition = element.scrollTop;
      }

      scroll.verticalScrollCalculation = undefined;
      this.$detectChanges();
    }, 150);
  }
}
